import { useEffect, useState } from 'react';
import { CircularProgress, Dialog, DialogContent, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TextBlock, TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { addTag, generateTagColor, tagIsUnique } from '../../../store/Tags';
import { TagColorPicker } from './TagColorPicker';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { DEFAULT_CANCEL_BUTTON_STYLE, DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';

export interface AddTagFormProps {
  accountId?: string;
}

export const AddTagForm = ({ accountId }: AddTagFormProps) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(generateTagColor());
  const [creatingTag, setCreatingTag] = useState(false);
  const { setSnack } = useSnackbar();

  const handleColorChange = (color: string) => {
    setColor(color);
  };

  const handleSubmit = async () => {
    if (!tagIsUnique({ name, color, id: '', accountId: '' })) {
      setSnack({ message: 'Tag with same name or color already exists', type: 'error', open: true });
      return;
    }
    setCreatingTag(true);
    await addTag({ name, color }, true, accountId);
    setCreatingTag(false);
    setOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setName('');
    setColor(generateTagColor());
  };

  const handleClose = () => {
    resetForm();
    setOpen(false);
  };

  useEffect(() => {
    if (accountId) setColor(generateTagColor());
  }, [accountId]);

  return (
    <>
      <button
        disabled={!accountId}
        className="flex items-center space-x-2"
        onClick={() => setOpen(true)}>
        <Icon type="plus" />
        <TextSpan
          size="sm"
          color="grey12">
          Add Tag
        </TextSpan>
      </button>
      <Dialog
        open={open}
        onClose={handleClose}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 4,
            color: (theme) => theme.palette.grey[500]
          }}>
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <div className="flex flex-col justify-between align-middle items-center px-2 w-full">
            <TextBlock
              size="sm14"
              weight="semibold">
              Add a Tag Name, choose color or customize
            </TextBlock>
            <form
              className="my-4 w-full"
              onSubmit={(e) => e.preventDefault()}>
              <TextField
                label="Tag Name"
                value={name}
                fullWidth
                style={{ fontSize: '.875rem', outline: '#3B6FE8' }}
                name="tag-name"
                onChange={(e) => setName(e.target.value)}
              />
              <TextBlock
                className="mt-4 mb-2"
                size="sm14"
                weight="semibold">
                Choose Tag Color:
              </TextBlock>
              <div className="flex flex-col items-center mt-4 w-full">
                <TagColorPicker
                  color={color}
                  onChange={handleColorChange}
                />
              </div>
              <div className="flex mt-4 justify-between">
                <button
                  onClick={handleClose}
                  className={DEFAULT_CANCEL_BUTTON_STYLE}>
                  Close
                </button>
                <button
                  onClick={handleSubmit}
                  className={DEFAULT_ADVANCE_BUTTON_STYLE}>
                  {creatingTag ? <CircularProgress size={24} /> : 'Create Tag'}
                </button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
